import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { CampaignDetailsAndAudience, CampaignCadenceSettings } from './CampaignFormFields';

export default function EmailCampaignBuilder() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [companyFilters, setCompanyFilters] = useState({ status: '__any__', contact_email: '', date_from: '', date_to: '', source: '__any__' });
    const [form, setForm] = useState({
        name: '', subject: '', body_html: '',
        client_ids: [], only_new_leads: true, keyword: '',
        verified_only: false, exclude_contacted: true, bounce_correction_import_id: null,
        cadence_seconds: 90, daily_cap: 100,
    });
    const [creating, setCreating] = useState(false);

    const loadCompanies = useCallback(async () => {
        try {
            const r = await api.get('/clients', { params: {
                search, per_page: 200,
                status: companyFilters.status === '__any__' ? undefined : companyFilters.status,
                contact_email: companyFilters.contact_email || undefined,
                date_from: companyFilters.date_from || undefined,
                date_to: companyFilters.date_to || undefined,
                source: companyFilters.source === '__any__' ? undefined : companyFilters.source,
            } });
            setCompanies(r.data.items);
        } catch { /* noop */ }
    }, [search, companyFilters]);
    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    const create = async () => {
        if (!form.name.trim()) return toast.error('Give the campaign a name');
        if (!form.subject.trim() || !form.body_html.trim()) return toast.error('Add a subject and body');
        setCreating(true);
        try {
            const r = await api.post('/leadgen/email-campaigns', {
                ...form,
                client_ids: form.client_ids.length ? form.client_ids : null,
            });
            toast.success('Campaign draft created');
            navigate(`/leadgen/email-campaigns/${r.data.id}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setCreating(false); }
    };

    return (
        <div data-testid="email-campaign-builder-page" className="bm-fade-in">
            <PageHeader
                title="New Email Campaign"
                description="Individual, personalised emails sent one at a time from your connected Microsoft 365 mailbox — never BCC."
                actions={<Button variant="outline" onClick={() => navigate('/leadgen/email-campaigns')} className="h-9"><ArrowLeft size={15} className="mr-1" /> Back</Button>}
            />
            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2">
                    <CampaignDetailsAndAudience form={form} setForm={setForm} companies={companies}
                        search={search} setSearch={setSearch} companyFilters={companyFilters} setCompanyFilters={setCompanyFilters} />
                </div>

                <div className="space-y-5">
                    <CampaignCadenceSettings form={form} setForm={setForm} />
                    <Button onClick={create} disabled={creating} className="w-full h-10" style={{ background: 'var(--bm-primary)' }} data-testid="campaign-create-draft-btn">
                        {creating ? 'Creating…' : 'Create draft — review & send next'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
